import React, { useState, useEffect } from 'react';

import { Button, Col, Row, Progress } from 'antd';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { IQuizDermoAnswer, IQuizDermoQuestion } from 'src/shared/models';
import { customFormatMessage, getTranslatedData } from 'utils';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import { UserResourceEnum } from 'src/shared/enums';

// TODO: sacar este Enum fuera del fichero
enum ButtonActionEnum {
  VERIFY_ANSWER = 1,
  NEXT_ANSWER = 2,
  FINALIZE_QUIZ = 3,
  REPEAT_QUIZ = 4,
}

interface IQuestion {
  saveAnswer: (idResourceAnswer: number) => void;
  handleSubmitQuizDermo: (
    idResourceAnswer: number,
    participationStatus: number,
  ) => void;
  //showStatistics: boolean;
  allowContinue: boolean;
  totalResponses: number;
  questions: IQuizDermoQuestion[];
}
export default function QuestionsAndAnswers({
  saveAnswer,
  handleSubmitQuizDermo,
  // ? User can do the next question without select the correct answer
  // ? If 'false', user must select the correct one prior to be allowed to continue
  allowContinue,
  questions,

  totalResponses,
}: //showStatistics,
IQuestion): JSX.Element {
  const { formatMessage, locale } = useIntl();
  const { IN_PROGRESS, DONE } = UserResourceEnum;
  //const [answerBoxClass, setAnswerBoxClass] = useState<string>('');
  const [userAnswer, setUserAnswer] = useState<null | IQuizDermoAnswer>(null);
  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [showCorrect, setShowCorrect] = useState<boolean>(false);
  //const [canContinue, setCanContinue] = useState<boolean>(false);
  const [answersSelectable, setAnswersSelectable] = useState<boolean>(true);
  const [question, setQuestion] = useState<IQuizDermoQuestion>(questions[0]);
  const [questionNumber, setQuestionNumber] = useState<number>(0);
  const [responses, setResponses] = useState<number[]>([]);
  const [progressBarPercentage, setProgressBarPercentage] = useState<number>(0);
  const [buttonAction, setButtonAction] = useState<ButtonActionEnum>(
    ButtonActionEnum.VERIFY_ANSWER,
  );
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [buttonLabel, setButtonLabel] = useState<string>(
    'formation.quiz-dermo.button.verify-answer',
  );
  const [canRepeatQuiz, setCanRepeatQuiz] = useState<boolean>(false);

  // ? Button has 2 behaviors:
  // ?    1 - Check if selected answer is valid or not
  // ?    2 - Pass to the next question
  // ?    3 - Finish the Quiz
  // ?    4 - Repeat the Quiz
  const handleButtonClick = async () => {
    /*
    const checkCorrection = answersSelectable;
    const goToNextQuestion = !question.isLastQuestion;
    */
    //const finalizeQuiz = !answersSelectable && question.isLastQuestion;

    switch (buttonAction) {
      case ButtonActionEnum.VERIFY_ANSWER:
        // ? Set answer box class to default
        setShowAnswer(true);
        setShowCorrect(true);

        if (question.isLastQuestion) {
          setAnswersSelectable(false);
          // ? Change the action and the label of the button
          setButtonLabel('formation.quiz-dermo.button.finalize-quiz');
          setButtonAction(ButtonActionEnum.FINALIZE_QUIZ);
        } else {
          setAnswersSelectable(false);
          // ? Change the action and the label of the button
          setButtonLabel('formation.quiz-dermo.button.next-question');
          setButtonAction(ButtonActionEnum.NEXT_ANSWER);

          // ? Save current answer result in user userResource (API)
          handleSubmitQuizDermo(userAnswer.idResource, IN_PROGRESS);
        }

        break;
      case ButtonActionEnum.NEXT_ANSWER:
        saveAnswer(userAnswer.idResource);
        setUserAnswer(null);
        setShowAnswer(false);
        setAnswersSelectable(true);
        setShowCorrect(false);
        setQuestionNumber(questionNumber + 1);
        setQuestion(questions[questionNumber + 1]);
        setResponses([]);

        // ? Change the action and the label of the button
        setButtonLabel('formation.quiz-dermo.button.verify-answer');
        setButtonAction(ButtonActionEnum.VERIFY_ANSWER);
        setButtonDisabled(true);
        break;
      case ButtonActionEnum.FINALIZE_QUIZ:
        handleSubmitQuizDermo(userAnswer.idResource, DONE);
        setUserAnswer(null);

        // ? Allow user to repeat the Quiz
        setCanRepeatQuiz(true);

        // ? Change the action and the label of the button
        setButtonLabel('formation.quiz-dermo.button.repeat-quiz');
        setButtonAction(ButtonActionEnum.REPEAT_QUIZ);

        break;
      case ButtonActionEnum.REPEAT_QUIZ:
        // ? Function required to force array state
        handleRepeatQuizDermo();
        setAnswersSelectable(true);
        setQuestionNumber(0);
        setUserAnswer(null);
        setShowAnswer(false);
        setShowCorrect(false);

        // ? Change the view to show question and answers again
        setCanRepeatQuiz(false);

        // ? Change the action and the label of the button
        setButtonLabel('formation.quiz-dermo.button.verify-answer');
        setButtonAction(ButtonActionEnum.VERIFY_ANSWER);
        setButtonDisabled(true);
        break;
    }
  };

  const handleSelectAnswer = (
    answerSelected: IQuizDermoAnswer,
    alreadyAnswered?: boolean,
  ) => {
    if (!answersSelectable) return;

    // FIXME: utilizar esto en caso de que se permitan multirespuesta
    //setResponses([...responses, answerSelected.idResource]);
    setResponses([answerSelected.idResource]);

    // ? Enable button to verify if selected answer is correct or not
    setButtonDisabled(false);

    if (alreadyAnswered) {
      // Show the selected answer and avoid user from selecting other answers, and show correct "next" button
      setUserAnswer(answerSelected);
      setShowAnswer(true);
      setAnswersSelectable(false);
      setShowCorrect(true);
      setButtonLabel('formation.quiz-dermo.button.next-question');
      setButtonAction(ButtonActionEnum.NEXT_ANSWER);
      return;
    }

    switch (allowContinue) {
      // ? User can continue without selecting the correct answer
      case true:
        setUserAnswer(answerSelected);
        // setShowAnswer(true);
        //setCanContinue(true);
        // setAnswersSelectable(false);
        // setShowCorrect(true);
        break;
      // ? User can continue without selecting the correct answer
      case false:
        setShowAnswer(true);
        setShowCorrect(answerSelected.isCorrect);
        setAnswersSelectable(!answerSelected.isCorrect);
        //setCanContinue(_answer.isCorrect);
        setUserAnswer(answerSelected.isCorrect ? answerSelected : null);
        break;
    }
  };

  const handleRepeatQuizDermo = () => {
    const newArr = [...questions];
    newArr[0] = questions[0];

    setQuestion(newArr[0]);
  };

  const renderAnswer = (answer: IQuizDermoAnswer, key: number) => {
    const { idResource, isCorrect, name, alreadyAnswered } = answer;

    const answerSelected = responses.includes(idResource);

    const answerVerificationResult =
      isCorrect && showCorrect ? 'correct' : 'incorrect';

    const answerSelectedClass =
      answerSelected && !showCorrect
        ? 'selected'
        : showAnswer && showCorrect && (answerSelected || isCorrect)
        ? answerVerificationResult
        : '';
    const answerCheckClass = showAnswer
      ? answerVerificationResult
      : answerSelectedClass;

    return (
      <Button
        type="text"
        disabled={!answersSelectable}
        onClick={() => handleSelectAnswer(answer)}
        className="question-answer-container"
        key={key}>
        <Row>
          <Col
            className={`question-answer question-answer--${answerSelectedClass} answer--${answerCheckClass}`}
            md={24}
            xs={24}>
            <Row justify="space-between">
              {/* 
            // * ---------------------------------------------  answer text
            */}
              <Col className="answer-title quiz-answer-text-wrap" xs={24} lg={24}>
                {customFormatMessage(
                  'formation.quiz-dermo.answer.prefix-{index}',
                  {
                    index: key + 1,
                  },
                )}
                {getTranslatedData(answer, 'content') || name}
              </Col>
              {/* 
            // * ---------------------------------------------  answer text: correct or incorrect
            */}
              <Col className={`answer--${answerCheckClass}`} xs={24} lg={24}>
                <Row justify="start" align="middle">
                  {/* 
                // * -----------   correct
                */}
                  {showCorrect && isCorrect && (
                    <div>
                      <CheckCircleOutlined
                        style={{
                          fontSize: '16px',
                          color: '#027C41',
                          marginRight: '0.5em',
                        }}
                      />
                      {formatMessage({
                        id: 'formation.quiz-dermo.answer.correct',
                      })}
                    </div>
                  )}
                  {/* 
                // ! -----------  incorrect
                */}
                  {showCorrect && !isCorrect && answerSelected && (
                    <div>
                      <CloseCircleOutlined
                        style={{
                          fontSize: '16px',
                          color: '#CD000F',
                          marginRight: '0.5em',
                        }}
                      />
                      {formatMessage({
                        id: 'formation.quiz-dermo.answer.incorrect',
                      })}
                    </div>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Button>
    );
  };

  const renderQuiz = (
    <Row
      justify="start"
      className="quizDermoQuestionsAndAnswers__quiz-question-container">
      {canRepeatQuiz ? (
        <>
          <Row className="repeat-quiz-title">
            {formatMessage({
              id: 'formation.quiz-dermo.repeat-quiz.title',
            })}
          </Row>
          <Row className="repeat-quiz-description">
            {formatMessage({
              id: 'formation.quiz-dermo.repeat-quiz.description',
            })}
          </Row>
          <Row>
            <div className="repeat-quiz-card-container">
              <span className="repeat-quiz-card-container--text">
                {formatMessage({
                  id: 'formation.quiz-dermo.repeat-quiz.label',
                })}
              </span>
            </div>
          </Row>
        </>
      ) : (
        <>
          {/* 
          // * ---------------------------------------------  quiz counter
          */}
          <Row className="question-counter" justify="start">
            {customFormatMessage(
              'formation.quiz-dermo.question.counter-{current}-{total}',
              {
                current: questionNumber + 1,
                total: questions.length,
              },
            )}

            <Progress
              className="complete-profile__prompt__progress"
              percent={((questionNumber + 1) / questions.length) * 100}
              showInfo={false}
              strokeWidth={12}
              strokeColor="#218500"
            />
          </Row>
          {/* 
          // * ---------------------------------------------  quiz questions & answers
          */}
          <Row>
            {/* 
            // * -----------  question
            */}
            <Row justify="start" align="middle">
              <Col md={24} xs={22}>
                <Row className="question-title">
                  {getTranslatedData(question, 'content') || question.title}
                </Row>
              </Col>
            </Row>
            {/* 
            // * -----------  answers (loop)
            */}
            {question?.answers?.map((answer, key: number) => {
              return renderAnswer(answer, key);
            })}
          </Row>
        </>
      )}

      {/* 
      // * --------------------------------------------- quiz button
      */}
      <Row className="button-container" gutter={4} justify="center">
        <Button
          inline-block
          //disabled={!canContinue}
          icon={canRepeatQuiz && <ReloadOutlined />}
          disabled={buttonDisabled}
          onClick={handleButtonClick}
          className="btn-primary btn-primary--black button-container__content">
          <span>
            <FormattedMessage id={buttonLabel} />
          </span>
        </Button>
      </Row>
    </Row>
  );

  // This useEffect will check if the user has already answered a question.
  // If so, we will execute the "handleSelectAnswer" function with a special parameter,
  // to correctly display the answered question and prevent the user from selecting anything else.
  useEffect(() => {
    const questionAlreadyAnswered = question.answers.find(
      (answer) => answer.alreadyAnswered,
    );

    if (questionAlreadyAnswered) {
      handleSelectAnswer(questionAlreadyAnswered, true);
    }
  }, [question]);

  return renderQuiz;
}
